import axios from 'axios';
import { apiConfig } from '@/utils/constants';
import { AppLogger } from '@/logger';
import type { IActionParams } from '@/store/modules/index';

const logger = new AppLogger('maintenance state');
const POLL_MS = 2 * 60 * 1000;

export interface MaintenanceStatus {
  available: boolean;
  reason: string | null;
  bannerMessage: string | null;
  scheduledEnd: string | null;
}

export interface MaintenanceInfo {
  available: boolean;
  reason?: string | null;
  bannerMessage: string | null;
  lmMessage: string;
  scheduledEnd: string | null;
}

export interface MaintenanceWindowDto {
  maintenanceWindowId: number;
  service: string;
  startsAtUtc: string;
  endsAtUtc: string;
  message: string | null;
  lmMessage: string | null;
  isActive: boolean;
  createdBy: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface AdminMaintenanceStatus {
  current: MaintenanceInfo | null;
  upcoming: MaintenanceWindowDto | null;
}

interface IMaintenanceState {
  status: MaintenanceStatus;
  adminStatus: AdminMaintenanceStatus;
}

const defaultStatus = (): MaintenanceStatus => ({
  available: true,
  reason: null,
  bannerMessage: null,
  scheduledEnd: null,
});

const initialState = (): IMaintenanceState => ({
  status: defaultStatus(),
  adminStatus: { current: null, upcoming: null },
});

// Module-scope so vuex-persistedstate never serialises a stale interval id.
let pollIntervalId: ReturnType<typeof setInterval> | null = null;

type ActionParams = IActionParams<IMaintenanceState>;

export default {
  namespaced: true,
  state: initialState,
  mutations: {
    SET_STATUS(s: IMaintenanceState, p: MaintenanceStatus) {
      s.status = p;
    },
    SET_ADMIN_STATUS(s: IMaintenanceState, p: AdminMaintenanceStatus) {
      s.adminStatus = p;
    },
    RESET_STATE(s: IMaintenanceState) {
      Object.assign(s, initialState());
    },
  },
  actions: {
    async poll({ commit, rootGetters }: ActionParams) {
      try {
        const { data } = await axios.get<MaintenanceStatus>(`${apiConfig.basePath}/api/maintenance/status`);
        commit('SET_STATUS', data);
      } catch (error) {
        logger.captureStoreError('poll/status', error);
      }
      if (rootGetters['login/isAdmin']) {
        try {
          const accessToken = rootGetters['login/accessToken'];
          const { data } = await axios.get<AdminMaintenanceStatus>(
            `${apiConfig.basePath}/api/maintenance/admin-status`,
            { headers: { Authorization: `Bearer ${accessToken}` } },
          );
          commit('SET_ADMIN_STATUS', data);
        } catch (error) {
          logger.captureStoreError('poll/adminStatus', error);
        }
      }
    },
    startPolling({ dispatch }: ActionParams) {
      if (pollIntervalId) {
        clearInterval(pollIntervalId);
      }
      dispatch('poll');
      pollIntervalId = setInterval(() => dispatch('poll'), POLL_MS);
    },
    stopPolling() {
      if (pollIntervalId) {
        clearInterval(pollIntervalId);
        pollIntervalId = null;
      }
    },
    resetState({ commit }: ActionParams) {
      commit('RESET_STATE');
    },
  },
  getters: {
    available: (s: IMaintenanceState) => s.status.available,
    reason: (s: IMaintenanceState) => s.status.reason,
    bannerMessage: (s: IMaintenanceState) => s.status.bannerMessage,
    scheduledEnd: (s: IMaintenanceState) => s.status.scheduledEnd,
    adminCurrent: (s: IMaintenanceState) => s.adminStatus.current,
    adminUpcoming: (s: IMaintenanceState) => s.adminStatus.upcoming,
  },
};
